import React, { useState } from 'react';
import { signInWithPhoneNumber, RecaptchaVerifier, ConfirmationResult } from 'firebase/auth';
import { auth, db } from '../../firebase.config';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../../styles/kyro-design.css';

const AdminLogin: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const navigate = useNavigate();

  const setupRecaptcha = () => {
    if (!(window as unknown as { recaptchaVerifier?: RecaptchaVerifier }).recaptchaVerifier) {
      (window as unknown as { recaptchaVerifier: RecaptchaVerifier }).recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {},
      });
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const digits = phoneNumber.replace(/\D/g, '');
      const withPlus = phoneNumber.startsWith('+')
        ? phoneNumber
        : digits.startsWith('91') && digits.length >= 12
          ? `+${digits}`
          : `+91${digits.length === 10 ? digits : digits.replace(/^91/, '') || digits}`;
      const altId = digits.length === 10 ? digits : (digits.startsWith('91') ? digits : `91${digits}`);
      let adminDoc = await getDoc(doc(db, 'adminUsers', withPlus));
      if (!adminDoc.exists()) {
        adminDoc = await getDoc(doc(db, 'adminUsers', altId));
      }
      if (!adminDoc.exists()) {
        setError('This phone number is not authorized as an admin.');
        setLoading(false);
        return;
      }
      setupRecaptcha();
      const appVerifier = (window as unknown as { recaptchaVerifier: RecaptchaVerifier }).recaptchaVerifier;
      const result = await signInWithPhoneNumber(auth, withPlus, appVerifier);
      setConfirmationResult(result);
      setStep('otp');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (confirmationResult) {
        await confirmationResult.confirm(otp);
        navigate('/admin-dashboard');
      }
    } catch {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="login-hero-min">
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner" style={{ textAlign: 'center', maxWidth: 'none' }}>
          <h1 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', marginBottom: '0.5rem' }}>Admin Login</h1>
          <p className="hero-sub" style={{ margin: '0 auto' }}>Secure access for authorized administrators</p>
          <div className="login-card-min">
            {step === 'phone' ? (
              <form onSubmit={handleSendOTP} className="c-form">
                <input
                  className="c-input"
                  type="tel"
                  aria-label="Admin phone number"
                  placeholder="+91 9876543210"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                />
                <button type="submit" className="c-submit" disabled={loading}>
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="c-form">
                <input
                  className="c-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label="6-digit OTP"
                  placeholder="6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  required
                />
                <button type="submit" className="c-submit" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button type="button" style={{ width: '100%', background: 'none', border: 'none', color: 'var(--gray)', fontSize: '0.85rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }} onClick={() => setStep('phone')}>
                  Change Number
                </button>
              </form>
            )}
            {error && <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#DC2626' }} role="alert" aria-live="assertive">{error}</div>}
            <div id="recaptcha-container" />
            <p style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: '1.2rem', lineHeight: 1.6 }}>
              Authorized admin only. Support: <a href="mailto:compliance@kyrocapital.in" style={{ color: 'var(--blue)' }}>compliance@kyrocapital.in</a> or <a href="tel:+917611111102" style={{ color: 'var(--blue)' }}>76 111 111 02</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
